// src/controllers/useBrandsSectionController.js
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL, BASE_IMAGE_URL } from "../utils/api";
import useIsMobile from "../hooks/useIsMobile";
import { lsGet, lsSet, lsDel } from "../hooks/expiry";

const CARDS_PER_PAGE = 10; // desktop: 2 rows * 5 columns
const BRAND_MOBILE_PAGE_SIZE = 6; // brand matrix (mobile)
const REST_MOBILE_PAGE_SIZE = 8;  // other steps matrix (mobile)
const PART_MOBILE_PAGE_SIZE = 4;  // parts matrix (mobile)

// LocalStorage key
const STORAGE_KEY = "BRANDS_STATE_V1";
const ONE_DAY = 24 * 60 * 60 * 1000;

export default function useBrandsSectionController(onBack) {
  // -------- Prefill (from localStorage)
  const [prefill, setPrefill] = useState(null);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [prefillBusy, setPrefillBusy] = useState(false);

  // NEW: لا نكتب على localStorage إلا بعد ما نجرّب نقرأ/نطبّق المحفوظ
  const [canPersist, setCanPersist] = useState(false);

  // -------- Loading states
  const [loading, setLoading] = useState({
    brands: true,
    models: false,
    years: false,
    carCategories: false,
    categories: false,
    subCategories: false,
    parts: false,
  });
  const setLoadingKey = (key, val) => setLoading((prev) => ({ ...prev, [key]: val }));

  // -------- Data buckets (+ originals for local filtering)
  const [brands, setBrands] = useState([]);
  const [originalBrands, setOriginalBrands] = useState([]);

  const [models, setModels] = useState([]);
  const [originalModels, setOriginalModels] = useState([]);

  const [years, setYears] = useState([]);
  const [originalYears, setOriginalYears] = useState([]);

  const [carCategories, setCarCategories] = useState([]);
  const [originalCarCategories, setOriginalCarCategories] = useState([]);

  const [categories, setCategories] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);

  const [subCategories, setSubCategories] = useState([]);
  const [originalSubCategories, setOriginalSubCategories] = useState([]);

  const [parts, setParts] = useState([]);
  const [originalParts, setOriginalParts] = useState([]);

  // -------- Selections
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedCarCategory, setSelectedCarCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // -------- Search + paging
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  // ---- Save chosen car category (session guard to prevent duplicate POSTs)
  const [savedCarCategoryIds, setSavedCarCategoryIds] = useState(() => new Set());
  const [savingCar, setSavingCar] = useState(false);

  // ---- Device & direction helpers
  const isMobile = useIsMobile(768);
  const [isRtl, setIsRtl] = useState(false);
  useEffect(() => {
    if (typeof document !== "undefined") {
      setIsRtl(document.documentElement.dir === "rtl" || document.body.dir === "rtl");
    }
  }, []);

  // -------- Helpers
  const load = async (url, key, onOk) => {
    setLoadingKey(key, true);
    try {
      const res = await axios.get(url);
      if (res?.data?.status) onOk(res.data.data);
    } catch (err) {
      console.error(`Failed to load (${key})`, err);
    } finally {
      setLoadingKey(key, false);
    }
  };

  const clearSearch = () => setSearchText("");
  const resetPaging = () => setCurrentPage(0);

  const resetAfter = (level) => {
    switch (level) {
      case "brand":
        setModels([]); setYears([]); setCarCategories([]); setCategories([]); setSubCategories([]); setParts([]);
        setSelectedModel(null); setSelectedYear(null); setSelectedCarCategory(null); setSelectedCategory(null); setSelectedSubCategory(null);
        break;
      case "model":
        setYears([]); setCarCategories([]); setCategories([]); setSubCategories([]); setParts([]);
        setSelectedYear(null); setSelectedCarCategory(null); setSelectedCategory(null); setSelectedSubCategory(null);
        break;
      case "year":
        setCarCategories([]); setCategories([]); setSubCategories([]); setParts([]);
        setSelectedCarCategory(null); setSelectedCategory(null); setSelectedSubCategory(null);
        break;
      case "carCategory":
        setCategories([]); setSubCategories([]); setParts([]);
        setSelectedCategory(null); setSelectedSubCategory(null);
        break;
      case "category":
        setSubCategories([]); setParts([]);
        setSelectedSubCategory(null);
        break;
      case "subCategory":
        setParts([]);
        break;
      default:
        break;
    }
    resetPaging();
  };

  const filterItems = (text, originalList, setter) => {
    if (!text.trim()) {
      setter(originalList);
      return;
    }
    const q = text.toLowerCase();
    const filtered = originalList.filter((it) =>
      (it.name || it.year || "").toLowerCase().includes(q)
    );
    setter(filtered);
  };

  // -------- Initial brands
  useEffect(() => {
    load(`${API_BASE_URL}/brands`, "brands", (data) => {
      setBrands(data);
      setOriginalBrands(data);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load prefill from localStorage once (old behavior)
  useEffect(() => {
    const raw = localStorage.getItem("BRANDS_PREFILL");
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      setPrefill(p);
      if (p?.brandId) setPrefillBusy(true);
    } catch (_) { }
  }, []);

  // -------- Local search
  const handleSearchChange = (e) => {
    console.log("Before search", {
      selectedBrand,
      selectedModel,
      selectedYear,
      selectedCarCategory,
      selectedCategory,
      selectedSubCategory,
    });
    const value = e.target.value;
    setSearchText(value);
    resetPaging();

    if (!selectedBrand) {
      filterItems(value, originalBrands, setBrands);
    } else if (!selectedModel) {
      filterItems(value, originalModels, setModels);
    } else if (!selectedYear) {
      filterItems(value, originalYears, setYears);
    } else if (!selectedCarCategory) {
      filterItems(value, originalCarCategories, setCarCategories);
    } else if (!selectedCategory) {
      filterItems(value, originalCategories, setCategories);
    } else if (!selectedSubCategory) {
      filterItems(value, originalSubCategories, setSubCategories);
    } else {
      filterItems(value, originalParts, setParts);
    }
  };

  // Save car category (only when logged-in; skip dupes in-session)
  const maybeSaveSelectedCarCategory = async (carCategoryId) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    if (savedCarCategoryIds.has(carCategoryId) || savingCar) return;

    try {
      setSavingCar(true);
      await axios.post(
        `${API_BASE_URL}/cars`,
        { car_category_id: carCategoryId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedCarCategoryIds((prev) => {
        const next = new Set(prev);
        next.add(carCategoryId);
        return next;
      });

    } catch (err) {
      if (axios.isAxiosError(err)) {
        const s = err.response?.status;
        if (s === 401) {
          console.warn("Not authorized to save car (401).");
        } else if (s === 409 || s === 422) {
          setSavedCarCategoryIds((prev) => {
            const next = new Set(prev);
            next.add(carCategoryId);
            return next;
          });
          console.warn("Car already saved or validation issue.");
        } else {
          console.error("Save car failed:", err.response?.data || err.message);
        }
      } else {
        console.error("Save car failed:", err);
      }
    } finally {
      setSavingCar(false);
    }
  };

  // -------- Click flows (unchanged)
  const handleBrandClick = (brandId) => {
    setSelectedBrand(brandId);
    clearSearch();
    resetAfter("brand");
    load(`${API_BASE_URL}/models/${brandId}`, "models", (data) => {
      setModels(data);
      setOriginalModels(data);
    });
  };

  const handleModelClick = (modelId) => {
    setSelectedModel(modelId);
    clearSearch();
    resetAfter("model");
    load(`${API_BASE_URL}/years/${modelId}`, "years", (data) => {
      const mapped = data.map((y) => ({ ...y, name: y.year }));
      setYears(mapped);
      setOriginalYears(mapped);
    });
  };

  const handleYearClick = (yearId) => {
    setSelectedYear(yearId);
    clearSearch();
    resetAfter("year");
    load(`${API_BASE_URL}/car-categories/${yearId}`, "carCategories", (data) => {
      const mapped = data.map((cat) => ({
        ...cat,
        icon: cat.icon ? `${BASE_IMAGE_URL}${cat.icon}` : "/src/assets/images/default.jpg",
      }));
      setCarCategories(mapped);
      setOriginalCarCategories(mapped);
    });
  };

  const handleCarCategoryClick = (carCategoryId) => {
    setSelectedCarCategory(carCategoryId);
    clearSearch();
    resetAfter("carCategory");
    maybeSaveSelectedCarCategory(carCategoryId);
    load(`${API_BASE_URL}/categories/${carCategoryId}`, "categories", (data) => {
      setCategories(data);
      setOriginalCategories(data);
    });
  };

  const handleCategoryClick = (categoryId, carCategoryId) => {
    setSelectedCategory(categoryId);
    clearSearch();
    resetAfter("category");

    load(`${API_BASE_URL}/subCategories/${carCategoryId}/${categoryId}`, "subCategories", (data) => {
      const mapped = data.map((sub) => ({
        ...sub,
        icon: sub.icon ? `${BASE_IMAGE_URL}${sub.icon}` : "/src/assets/images/default.jpg",
      }));
      setSubCategories(mapped);
      setOriginalSubCategories(mapped);
    });
  };

  const handleSubCategoryClick = (subCategoryId) => {
    setSelectedSubCategory(subCategoryId);
    clearSearch();
    resetAfter("subCategory");
    load(
      `${API_BASE_URL}/parts?sub_category_id=${subCategoryId}&car_category_id=${selectedCarCategory}`,
      "parts",
      (data) => {
        setParts(data);
        setOriginalParts(data);
      }
    );
  };

  // Helper: single dispatcher for non-brand/non-parts grid
  const handleGridItemClick = useCallback(
    (id) => {
      if (!selectedModel) return handleModelClick(id);
      if (!selectedYear) return handleYearClick(id);
      if (!selectedCarCategory) return handleCarCategoryClick(id);
      if (!selectedCategory) return handleCategoryClick(id, selectedCarCategory);
      if (!selectedSubCategory) return handleSubCategoryClick(id);
    },
    [selectedModel, selectedYear, selectedCarCategory, selectedCategory, selectedSubCategory]
  );

  // -------- Back stepper
  // -------- Back stepper (now also updates saved progress) --------
  const handleBackClick = () => {
    resetPaging();

    // read current saved snapshot (if any)
    const savedSnap = readSaved() || {
      brandId: null,
      modelId: null,
      yearId: null,
      carCategoryId: null,
      categoryId: null,
      subCategoryId: null,
    };

    if (selectedSubCategory) {
      // back from parts -> subcategories
      setSelectedSubCategory(null);
      setParts([]);
      writeSaved({ ...savedSnap, subCategoryId: null });
      return;
    }

    if (selectedCategory) {
      // back from subcategories -> categories
      setSelectedCategory(null);
      setSubCategories([]);
      writeSaved({ ...savedSnap, categoryId: null, subCategoryId: null });
      return;
    }

    if (selectedCarCategory) {
      // back from categories -> car categories
      setSelectedCarCategory(null);
      setCategories([]);
      writeSaved({
        ...savedSnap,
        carCategoryId: null,
        categoryId: null,
        subCategoryId: null,
      });
      return;
    }

    if (selectedYear) {
      // back from car categories -> years
      setSelectedYear(null);
      setCarCategories([]);
      writeSaved({
        ...savedSnap,
        yearId: null,
        carCategoryId: null,
        categoryId: null,
        subCategoryId: null,
      });
      return;
    }

    if (selectedModel) {
      // back from years -> models
      setSelectedModel(null);
      setYears([]);
      writeSaved({
        ...savedSnap,
        modelId: null,
        yearId: null,
        carCategoryId: null,
        categoryId: null,
        subCategoryId: null,
      });
      return;
    }

    if (selectedBrand) {
      // back from models -> brands
      setSelectedBrand(null);
      setModels([]);
      lsDel(STORAGE_KEY);
      // if everything is null, you can also clear storage entirely:
      // localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // already at the first step
    onBack && onBack();
  };


  // -------- Active list per step
  let list = brands;
  if (selectedBrand && !selectedModel) list = models;
  else if (selectedModel && !selectedYear) list = years;
  else if (selectedYear && !selectedCarCategory) list = carCategories;
  else if (selectedCarCategory && !selectedCategory) list = categories;
  else if (selectedCategory && !selectedSubCategory) list = subCategories;
  else if (selectedSubCategory) list = parts;

  // -------- Loading flag per step
  const activeLoading = useMemo(() => {
    if (!selectedBrand) return loading.brands;
    if (selectedBrand && !selectedModel) return loading.models;
    if (selectedModel && !selectedYear) return loading.years;
    if (selectedYear && !selectedCarCategory) return loading.carCategories;
    if (selectedCarCategory && !selectedCategory) return loading.categories;
    if (selectedCategory && !selectedSubCategory) return loading.subCategories;
    if (selectedSubCategory) return loading.parts;
    return false;
  }, [
    selectedBrand, selectedModel, selectedYear, selectedCarCategory, selectedCategory, selectedSubCategory, loading,
  ]);

  // -------- Title
  const title =
    selectedSubCategory
      ? "اختر القطعة اللي تحتاجها"
      : selectedCategory
        ? "تصنيف القطعة ؟"
        : selectedCarCategory
          ? "وش نوع القطعة اللي تدورها؟"
          : selectedYear
            ? "وش فئة سيارتك؟"
            : selectedModel
              ? "سنة الصنع"
              : selectedBrand
                ? "وش نوع سيارتك ؟"
                : "وش براند سيارتك؟";

  // -------- Desktop paging
  const isBrandStep = !selectedBrand;
  const pageSize = selectedSubCategory ? 3 : CARDS_PER_PAGE;
  const totalPages = Math.ceil(list.length / pageSize) || 1;
  const start = currentPage * pageSize;
  const end = start + pageSize;
  const paginatedList = isBrandStep ? list : list.slice(start, end);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedBrand, selectedModel, selectedYear, selectedCarCategory, selectedCategory, selectedSubCategory]);

  const canPrev = !isBrandStep && currentPage > 0;
  const canNext = !isBrandStep && currentPage < totalPages - 1;
  const prevPage = () => { if (canPrev) setCurrentPage((p) => p - 1); };
  const nextPage = () => { if (canNext) setCurrentPage((p) => p + 1); };

  // ===== Mobile pagination groups & swipe handlers =====
  const brandMobileTotalPages = Math.max(1, Math.ceil(list.length / BRAND_MOBILE_PAGE_SIZE));
  const brandMobilePageItems = useMemo(() => {
    const s = currentPage * BRAND_MOBILE_PAGE_SIZE;
    return list.slice(s, s + BRAND_MOBILE_PAGE_SIZE);
  }, [list, currentPage]);
  const bCanPrev = currentPage > 0;
  const bCanNext = currentPage < brandMobileTotalPages - 1;

  const restMobileTotalPages = Math.max(1, Math.ceil(list.length / REST_MOBILE_PAGE_SIZE));
  const restMobilePageItems = useMemo(() => {
    const s = currentPage * REST_MOBILE_PAGE_SIZE;
    return list.slice(s, s + REST_MOBILE_PAGE_SIZE);
  }, [list, currentPage]);
  const rCanPrev = currentPage > 0;
  const rCanNext = currentPage < restMobileTotalPages - 1;

  const partsMobileTotalPages = Math.max(1, Math.ceil(list.length / PART_MOBILE_PAGE_SIZE));
  const partsMobilePageItems = useMemo(() => {
    const s = currentPage * PART_MOBILE_PAGE_SIZE;
    return list.slice(s, s + PART_MOBILE_PAGE_SIZE);
  }, [list, currentPage]);
  const pCanPrev = currentPage > 0;
  const pCanNext = currentPage < partsMobileTotalPages - 1;

  const createSwipeEnd = useCallback(
    (canPrevLocal, canNextLocal, totalPagesLocal) => (_, info) => {
      const { offset, velocity } = info;
      const dx = offset.x;
      const vx = velocity.x;
      const power = Math.abs(dx) + Math.abs(vx) * 0.2;
      const THRESH = 150;
      const nextByDir = isRtl ? dx > 0 || vx > 300 : dx < 0 || vx < -300;
      const prevByDir = isRtl ? dx < 0 || vx < -300 : dx > 0 || vx > 300;
      if (power > THRESH) {
        if (nextByDir && canNextLocal) {
          setCurrentPage((p) => Math.min(totalPagesLocal - 1, p + 1));
          return;
        }
        if (prevByDir && canPrevLocal) setCurrentPage((p) => Math.max(0, p - 1));
      }
    },
    [isRtl]
  );

  const handleBrandMobileSwipeEnd = createSwipeEnd(bCanPrev, bCanNext, brandMobileTotalPages);
  const handleRestMobileSwipeEnd = createSwipeEnd(rCanPrev, rCanNext, restMobileTotalPages);
  const handlePartsMobileSwipeEnd = createSwipeEnd(pCanPrev, pCanNext, partsMobileTotalPages);

  // Clamp page when switching steps/screen size (mobile UX nicety)
  useEffect(() => {
    if (isBrandStep && isMobile) setCurrentPage((p) => Math.min(p, brandMobileTotalPages - 1));
  }, [isBrandStep, isMobile, brandMobileTotalPages]);
  useEffect(() => {
    if (!isBrandStep && !selectedSubCategory && isMobile)
      setCurrentPage((p) => Math.min(p, restMobileTotalPages - 1));
  }, [isBrandStep, selectedSubCategory, isMobile, restMobileTotalPages]);
  useEffect(() => {
    if (selectedSubCategory && isMobile)
      setCurrentPage((p) => Math.min(p, partsMobileTotalPages - 1));
  }, [selectedSubCategory, isMobile, partsMobileTotalPages]);

  // ===== Other Part Suggestion API =====
  const submitPartSuggestion = useCallback(
    async (name) => {
      const body = {
        name,
        car_category_id: selectedCarCategory,
        part_category_id: selectedCategory,
        subcategory_id: selectedSubCategory,
      };

      try {
        const token = (localStorage.getItem("token") || "").trim();

        const url = token
          ? `${API_BASE_URL}/part-suggestions/me`
          : `${API_BASE_URL}/part-suggestions`;

        const config = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined;

        await axios.post(url, body, config);

        alert("✅ تم إرسال اقتراح القطعة بنجاح!");
        return true;
      } catch (err) {
        console.error("Part suggestion failed:", err);
        alert("❌ لم نتمكن من إرسال اقتراحك. حاول مرة أخرى.");
        return false;
      }
    },
    [selectedCarCategory, selectedCategory, selectedSubCategory]
  );


  // ===== LocalStorage helpers =====
  const readSaved = () => lsGet(STORAGE_KEY);

  const writeSaved = (snap, ttlMs = ONE_DAY) => {
    try {
      const clean = {
        brandId: snap.brandId ?? null,
        modelId: snap.modelId ?? null,
        yearId: snap.yearId ?? null,
        carCategoryId: snap.carCategoryId ?? null,
        categoryId: snap.categoryId ?? null,
        subCategoryId: snap.subCategoryId ?? null,
      };
      lsSet(STORAGE_KEY, clean, ttlMs); // 1-day TTL
    } catch { /* no-op */ }
  };

  // Persist snapshot whenever selections change — لكن فقط بعد التهيئة
  useEffect(() => {
    if (!canPersist) return; // لا تكتب قبل ما نجرّب نقرأ/نطبّق المحفوظ
    const anySelected = [
      selectedBrand,
      selectedModel,
      selectedYear,
      selectedCarCategory,
      selectedCategory,
      selectedSubCategory,
    ].some(Boolean);
    if (!anySelected) return; // لا تمسح التخزين بقيم null على أول رندر
    writeSaved({
      brandId: selectedBrand,
      modelId: selectedModel,
      yearId: selectedYear,
      carCategoryId: selectedCarCategory,
      categoryId: selectedCategory,
      subCategoryId: selectedSubCategory,
    });
  }, [
    canPersist,
    selectedBrand,
    selectedModel,
    selectedYear,
    selectedCarCategory,
    selectedCategory,
    selectedSubCategory,
  ]);

  // ====== Apply prefill (old) OR saved progress (new) once brands are ready ======
  useEffect(() => {
    if (!brands || brands.length === 0) return;

    (async () => {
      try {
        // 1) لو في BRANDS_PREFILL شغّله أولاً (سلوكك القديم)
        if (prefill && !prefillApplied) {
          await applyPrefill(prefill);
          return; // نطلع بدون تفعيل canPersist حتى ينتهي applyPrefill نفسه
        }

        // 2) لو ما في prefill — جرّب تقرأ الحالة المحفوظة
        const saved = readSaved();
        if (saved?.brandId) {
          await applySavedProgress(saved);
        }
      } finally {
        // الآن نسمح بالكتابة بعد ما جرّبنا القراءة/التطبيق
        setCanPersist(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, prefill, prefillApplied]);

  // ===== Your original prefill chain (kept as-is) =====
  const applyPrefill = async (p) => {
    if (!p?.brandId) return;

    setPrefillBusy(true);
    try {
      setPrefillApplied(false);

      // 1) Brand
      setSelectedBrand(p.brandId);
      resetAfter("brand");
      const modelsRes = await axios.get(`${API_BASE_URL}/models/${p.brandId}`);
      const modelsData = modelsRes?.data?.data ?? [];
      setModels(modelsData);
      setOriginalModels(modelsData);

      // 2) Model
      if (!p.modelId) return;
      setSelectedModel(p.modelId);
      resetAfter("model");
      const yearsRes = await axios.get(`${API_BASE_URL}/years/${p.modelId}`);
      const yearsData = (yearsRes?.data?.data ?? []).map((y) => ({ ...y, name: y.year }));
      setYears(yearsData);
      setOriginalYears(yearsData);

      // 3) Year
      if (!p.yearId) return;
      setSelectedYear(p.yearId);
      resetAfter("year");
      const carsRes = await axios.get(`${API_BASE_URL}/car-categories/${p.yearId}`);
      const carCatsData = (carsRes?.data?.data ?? []).map((cat) => ({
        ...cat,
        icon: cat.icon ? `${BASE_IMAGE_URL}${cat.icon}` : "/src/assets/images/default.jpg",
      }));
      setCarCategories(carCatsData);
      setOriginalCarCategories(carCatsData);

      // 4) Car Category
      if (p.carCategoryId) {
        setSelectedCarCategory(p.carCategoryId);
        resetAfter("carCategory");
        await maybeSaveSelectedCarCategory(p.carCategoryId);

        const catsRes = await axios.get(`${API_BASE_URL}/categories/${p.carCategoryId}`);
        const catsData = catsRes?.data?.data ?? [];
        setCategories(catsData);
        setOriginalCategories(catsData);
      }

      setPrefillApplied(true);
      localStorage.removeItem("BRANDS_PREFILL");
    } catch (err) {
      console.error("Failed to apply prefill", err);
    } finally {
      setPrefillBusy(false);
      setCanPersist(true); // بعد إنهاء prefill، اسمح بالكتابة
    }
  };

  // ====== Apply saved progress (new) ======
  const applySavedProgress = async (s) => {
    if (!s?.brandId) return;
    setPrefillBusy(true);
    try {
      // 1) Brand
      setSelectedBrand(s.brandId);
      resetAfter("brand");
      const modelsRes = await axios.get(`${API_BASE_URL}/models/${s.brandId}`);
      const modelsData = modelsRes?.data?.data ?? [];
      setModels(modelsData);
      setOriginalModels(modelsData);

      // 2) Model
      if (!s.modelId) return;
      setSelectedModel(s.modelId);
      resetAfter("model");
      const yearsRes = await axios.get(`${API_BASE_URL}/years/${s.modelId}`);
      const yearsData = (yearsRes?.data?.data ?? []).map((y) => ({ ...y, name: y.year }));
      setYears(yearsData);
      setOriginalYears(yearsData);

      // 3) Year
      if (!s.yearId) return;
      setSelectedYear(s.yearId);
      resetAfter("year");
      const carsRes = await axios.get(`${API_BASE_URL}/car-categories/${s.yearId}`);
      const carCatsData = (carsRes?.data?.data ?? []).map((cat) => ({
        ...cat,
        icon: cat.icon ? `${BASE_IMAGE_URL}${cat.icon}` : "/src/assets/images/default.jpg",
      }));
      setCarCategories(carCatsData);
      setOriginalCarCategories(carCatsData);

      // 4) Car Category
      if (!s.carCategoryId) return;
      setSelectedCarCategory(s.carCategoryId);
      resetAfter("carCategory");
      await maybeSaveSelectedCarCategory(s.carCategoryId);

      const catsRes = await axios.get(`${API_BASE_URL}/categories/${s.carCategoryId}`);
      const catsData = catsRes?.data?.data ?? [];
      setCategories(catsData);
      setOriginalCategories(catsData);

      // 5) Category
      if (!s.categoryId) return;
      setSelectedCategory(s.categoryId);
      resetAfter("category");

      const subsRes = await axios.get(`${API_BASE_URL}/subCategories/${s.carCategoryId}/${s.categoryId}`);
      const subsData = (subsRes?.data?.data ?? []).map((sub) => ({
        ...sub,
        icon: sub.icon ? `${BASE_IMAGE_URL}${sub.icon}` : "/src/assets/images/default.jpg",
      }));
      setSubCategories(subsData);
      setOriginalSubCategories(subsData);

      // 6) SubCategory → parts
      if (!s.subCategoryId) return;
      setSelectedSubCategory(s.subCategoryId);
      resetAfter("subCategory");

      const partsRes = await axios.get(
        `${API_BASE_URL}/parts?sub_category_id=${s.subCategoryId}&car_category_id=${s.carCategoryId}`
      );
      const partsData = partsRes?.data?.data ?? [];
      setParts(partsData);
      setOriginalParts(partsData);
    } catch (err) {
      console.error("Failed to apply saved progress", err);
    } finally {
      setPrefillBusy(false);
    }
  };

  // --- Jump helpers (no fake setters; only real state) ---

  // Always reset paging & search when jumping
  const resetUiForJump = () => {
    setCurrentPage(0);
    setSearchText("");
  };

  /** Step 1: Brand grid */
  const goToBrandStep = () => {
    resetUiForJump();
    // clear all selections
    setSelectedBrand(null);
    setSelectedModel(null);
    setSelectedYear(null);
    setSelectedCarCategory(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    // clear all dependent lists except brands (already loaded)
    setModels([]); setYears([]); setCarCategories([]);
    setCategories([]); setSubCategories([]); setParts([]);
    writeSaved({
      brandId: null,
      modelId: null,
      yearId: null,
      carCategoryId: null,
      categoryId: null,
      subCategoryId: null,
    });
  };

  /** Step 2: Model grid (keep brand, clear deeper; (re)load models if needed) */
  const goToModelStep = () => {
    if (!selectedBrand) return goToBrandStep();
    resetUiForJump();

    setSelectedModel(null);
    setSelectedYear(null);
    setSelectedCarCategory(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);

    setYears([]); setCarCategories([]); setCategories([]); setSubCategories([]); setParts([]);

    if (!originalModels.length) {
      load(`${API_BASE_URL}/models/${selectedBrand}`, "models", (data) => {
        setModels(data);
        setOriginalModels(data);
      });
    } else {
      setModels(originalModels);
    }
  };

  /** Step 3: Year grid (keep brand+model; (re)load years if needed) */
  const goToYearStep = () => {
    if (!selectedBrand) return goToBrandStep();
    if (!selectedModel) return goToModelStep();
    resetUiForJump();

    setSelectedYear(null);
    setSelectedCarCategory(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);

    setCarCategories([]); setCategories([]); setSubCategories([]); setParts([]);

    if (!originalYears.length) {
      load(`${API_BASE_URL}/years/${selectedModel}`, "years", (data) => {
        const mapped = data.map((y) => ({ ...y, name: y.year }));
        setYears(mapped);
        setOriginalYears(mapped);
      });
    } else {
      setYears(originalYears);
    }
  };

  /** Step 4: Car category grid (keep brand+model+year; (re)load car categories if needed) */
  const goToCarCategoryStep = () => {
    if (!selectedBrand) return goToBrandStep();
    if (!selectedModel) return goToModelStep();
    if (!selectedYear) return goToYearStep();
    resetUiForJump();

    setSelectedCarCategory(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);

    setCategories([]); setSubCategories([]); setParts([]);

    if (!originalCarCategories.length) {
      load(`${API_BASE_URL}/car-categories/${selectedYear}`, "carCategories", (data) => {
        const mapped = data.map((cat) => ({
          ...cat,
          icon: cat.icon ? `${BASE_IMAGE_URL}${cat.icon}` : "/src/assets/images/default.jpg",
        }));
        setCarCategories(mapped);
        setOriginalCarCategories(mapped);
      });
    } else {
      setCarCategories(originalCarCategories);
    }
  };

  /** Step 5/6: Part category / subcategory grid (keep car category; (re)load categories if needed) */
  const goToPartCategoryStep = () => {
    if (!selectedBrand) return goToBrandStep();
    if (!selectedModel) return goToModelStep();
    if (!selectedYear) return goToYearStep();
    if (!selectedCarCategory) return goToCarCategoryStep();
    resetUiForJump();

    // clearing both category & subcategory takes us to the category grid
    setSelectedCategory(null);
    setSelectedSubCategory(null);

    setSubCategories([]); setParts([]);

    if (!originalCategories.length) {
      load(`${API_BASE_URL}/categories/${selectedCarCategory}`, "categories", (data) => {
        setCategories(data);
        setOriginalCategories(data);
      });
    } else {
      setCategories(originalCategories);
    }
  };

  /** Step 5/6: Part category / subcategory grid (keep car category; (re)load categories if needed) */
  const goToPartSubCategoryStep = () => {
    if (!selectedBrand) return goToBrandStep();
    if (!selectedModel) return goToModelStep();
    if (!selectedYear) return goToYearStep();
    if (!selectedCarCategory) return goToCarCategoryStep();
    if (!selectedCategory) return goToPartCategoryStep();
    resetUiForJump();

    setSelectedSubCategory(null);

    setSubCategories([]); setParts([]);

    if (!originalSubCategories.length) {
      load(`${API_BASE_URL}/subCategories/${selectedCarCategory}/${selectedCategory}`, "subCategories", (data) => {
        const mapped = data.map((sub) => ({
          ...sub,
          icon: sub.icon ? `${BASE_IMAGE_URL}${sub.icon}` : "/src/assets/images/default.jpg",
        }));
        setSubCategories(mapped);
        setOriginalSubCategories(mapped);
      });
    } else {
      setSubCategories(originalSubCategories);
    }
  };


  return {
    // data (needed by details header)
    brands, models, years, carCategories, categories, subCategories,

    // selections
    selectedBrand, selectedModel, selectedYear, selectedCarCategory, selectedCategory, selectedSubCategory,

    // loading
    loading, activeLoading, prefillBusy, prefillApplied,

    // search
    searchText, handleSearchChange,

    // click handlers
    handleBrandClick, handleModelClick, handleYearClick, handleCarCategoryClick, handleCategoryClick, handleSubCategoryClick,
    handleGridItemClick,

    // back
    handleBackClick,

    // desktop paging
    isBrandStep, list, currentPage, setCurrentPage, paginatedList, totalPages, pageSize, canPrev, canNext, prevPage, nextPage,

    // mobile flags/helpers
    isMobile,

    // brand step (mobile)
    brandMobilePageItems, brandMobileTotalPages, bCanPrev, bCanNext, handleBrandMobileSwipeEnd,

    // rest steps (mobile)
    restMobilePageItems, restMobileTotalPages, rCanPrev, rCanNext, handleRestMobileSwipeEnd,

    // parts step (mobile)
    partsMobilePageItems, partsMobileTotalPages, pCanPrev, pCanNext, handlePartsMobileSwipeEnd,

    // title
    title,
    selectedCategory,

    // api
    submitPartSuggestion,
    goToBrandStep, goToModelStep, goToYearStep, goToCarCategoryStep, goToPartCategoryStep, goToPartSubCategoryStep
  };
}
